import type { UIMessage } from 'ai';

/**
 * FRONT-1423: the one, dependency-free place that turns a `generate_image`
 * tool result — or a plain file id already known from a message's own
 * attachment metadata — into the URL the files API serves it from. No React,
 * no icons, no query hooks — mirrors the `plan-status.ts` precedent
 * (FRONT-1412) so the phone surface can import it without dragging in
 * `message-bubble.tsx`'s whole dependency tree (cytoscape and friends).
 * `generated-image-card.tsx` (desktop) and `pwa-generated-image.tsx` (phone)
 * both import from here rather than re-deriving the same expression.
 */

export interface GeneratedImageOutput {
  fileId?: string;
  url?: string;
  model?: string;
  prompt?: string;
  revisedPrompt?: string;
  error?: string;
}

/** The raw-bytes URL a file id is served from — shared by a generated image
 *  (when the tool result carries no explicit `url`) and a user's own sent
 *  attachment, which only ever has a `fileId`. */
export function rawFileUrl(fileId: string): string {
  return `/api/files/${encodeURIComponent(fileId)}/raw`;
}

/** `generate_image`'s own source rule: prefer the explicit `url` the tool
 *  returned, else derive one from the `fileId`. Empty string means "nothing
 *  to show" — callers pair this with `output.error` to decide the failure
 *  state. */
export function generatedImageSrc(output: GeneratedImageOutput): string {
  return output.url ?? (output.fileId ? rawFileUrl(output.fileId) : '');
}

/** The caption text a generated-image card shows underneath the picture:
 *  the model's revised prompt if it rewrote one, else the original prompt,
 *  else nothing. Can be an empty string — callers only render a caption
 *  block when it's truthy. */
export function generatedImageCaption(output: GeneratedImageOutput): string {
  return output.revisedPrompt ?? output.prompt ?? '';
}

/** The `<img alt>` for a generated image — the caption when there is one,
 *  else a plain constant. Never empty, so the picture always has a name. */
export function generatedImageAlt(output: GeneratedImageOutput): string {
  return generatedImageCaption(output) || 'Generated image';
}

/** Attachment metadata a user message carries — set by `useAgentChat`'s
 *  `metadata.attachments` on send (`~/hooks/use-agent-chat.ts`) and echoed
 *  back unchanged by the `/history` route on reload, so a freshly-sent and a
 *  reloaded message go through the identical shape. */
export interface MessageAttachment {
  fileId: string;
  name: string;
  contentType: string;
  kind: string;
  /** FRONT-1567: 'document' when this attachment is a picked Knowledge
   *  document rather than an uploaded file — for a document, `fileId` is
   *  the document's FILENAME, not a `user_files.id` (see
   *  `ChatAttachment`'s docblock in `chat-input.tsx` for the wire
   *  contract). Absent/anything else means upload, matching every message
   *  sent before this ticket. Read by `attachmentHref` below — losing this
   *  field is what sent a document's own chip to the upload detail route. */
  source?: 'upload' | 'document';
}

/** Read the attachments a user message carries, from `metadata.attachments`.
 *  Returns an empty array when the message has none. One reader for both
 *  surfaces (FRONT-1423) so a shape change can't drift between them. */
export function readMessageAttachments(message: UIMessage): MessageAttachment[] {
  const meta = (message as unknown as { metadata?: { attachments?: unknown } }).metadata;
  if (!Array.isArray(meta?.attachments)) return [];
  return meta.attachments.filter(
    (a): a is MessageAttachment =>
      a !== null &&
      typeof a === 'object' &&
      typeof (a as MessageAttachment).fileId === 'string',
  );
}

/**
 * FRONT-1567: the chat bubble's own attachment-chip href. A picked document
 * uses its FILENAME as `fileId` (see `MessageAttachment.source`'s
 * docblock), and the document detail page lives at `/files/doc/:filename`
 * (`routes.tsx`) — `/files/:id` is the UPLOAD route, and
 * `routes/files.tsx`'s `modeFromLocation` has no filename heuristic to
 * redirect a misrouted id, so getting this wrong lands the user's own click
 * on a page that can't render it.
 */
export function attachmentHref(a: Pick<MessageAttachment, 'fileId' | 'source'>): string {
  return a.source === 'document'
    ? `/files/doc/${encodeURIComponent(a.fileId)}`
    : `/files/${a.fileId}`;
}
